import { AppIcon, BackrsCard, visuallyHiddenSx } from '@backrs_ui/common-ui';

import { Box, Typography, Skeleton, SxProps, Theme, IconButton } from '@mui/material';

import {
  RelevanceResultItem,
  RelevanceRating,
  RankedResultsListProps,
  RelevanceMode,
  RELEVANCE_COLORS,
} from '../types';

/**
 * RankedResultsList - Displays ranked results with inline rationales
 *
 * Features:
 * - Results displayed as bordered cards with featured icon
 * - Mode-dependent icon color (orange for majors, blue for career paths)
 * - Type label above title ("Major" in majors mode, "Career Path" in paths mode)
 * - External link icon in top-right corner
 * - Bordered rating badges with colored dot indicators
 * - Rationales displayed directly (not collapsed)
 * - Visual muting for low-relevance items
 *
 * @example
 * ```tsx
 * <RankedResultsList
 *   results={results}
 *   mode="majors"
 *   selections={['Psychology', 'Biology']}
 *   onItemClick={(item) => console.log('Clicked:', item.name)}
 *   loading={isLoading}
 * />
 * ```
 */
export const RankedResultsList = ({
  results,
  mode,
  selections,
  onItemClick,
  loading = false,
  emptyMessage = 'No results found',
}: RankedResultsListProps) => {
  // Check if all ratings for an item are "Low"
  const isAllLowRelevance = (item: RelevanceResultItem): boolean => {
    const ratings = Object.values(item.ratings);

    return ratings.length > 0 && ratings.every((rating) => rating === 'Low');
  };

  if (loading) {
    return (
      <Box sx={containerSx}>
        {[1, 2, 3].map((i) => (
          <BackrsCard key={i} sx={cardSx}>
            <Box sx={cardContentWrapperSx}>
              <Box sx={headerRowSx}>
                <Skeleton
                  variant="rectangular"
                  width={56}
                  height={56}
                  sx={{ borderRadius: '12px' }}
                />
                <Box sx={headerTextColumnSx}>
                  <Skeleton variant="text" width="20%" height={18} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
              </Box>
              <Box sx={rationaleContainerSx}>
                <Box sx={rationaleItemSx}>
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={28}
                    sx={{ borderRadius: '10px' }}
                  />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
              </Box>
            </Box>
          </BackrsCard>
        ))}
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box sx={emptyContainerSx}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      {results.map((item, index) => {
        const isMuted = isAllLowRelevance(item);
        const itemsWithRationales = selections.filter((s) => item.rationales[s]);

        return (
          <BackrsCard key={item.name} sx={getCardSx(isMuted)}>
            {/* Wrapper Box for position: relative - contains absolute positioned children */}
            <Box sx={cardContentWrapperSx}>
              {/* Header row: Icon + Text + External Link */}
              <Box sx={headerRowSx}>
                <Box sx={getIconContainerSx(mode)}>
                  <AppIcon iconName="Stand" diameter="28px" color="Gray/900" />
                </Box>
                <Box sx={headerTextColumnSx}>
                  {/* Type label - indicates what the result represents */}
                  <Typography sx={typeLabelSx}>
                    {mode === 'majors' ? 'Major' : 'Career Path'}
                  </Typography>
                  <Typography
                    component="h3"
                    sx={titleSx}
                    onClick={
                      onItemClick
                        ? (e) => {
                            e.stopPropagation();
                            onItemClick(item);
                          }
                        : undefined
                    }
                  >
                    {item.name}
                  </Typography>
                </Box>
                {/* External link icon - aligned right in header row */}
                {onItemClick && (
                  <IconButton
                    id={`ranked-result-details-${index}`}
                    sx={externalLinkSx}
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemClick(item);
                    }}
                    aria-label={`View details for ${item.name}`}
                  >
                    <AppIcon
                      iconName="ArrowCircleUpRight"
                      diameter="24px"
                      color="Colors/Base/Black"
                      strokeWidth="thin"
                    />
                  </IconButton>
                )}
              </Box>

              {/* Rationales - Always visible, not collapsed */}
              {itemsWithRationales.length > 0 && (
                <Box sx={rationaleContainerSx}>
                  {itemsWithRationales.map((selection) => {
                    const rationale = item.rationales[selection];
                    const rating = item.ratings[selection];

                    if (!rationale || !rating) return null;

                    return (
                      <Box key={selection} sx={rationaleItemSx}>
                        {/* Badge group: Rating badge + selection name */}
                        <Box sx={badgeGroupSx}>
                          <Box sx={ratingBadgeSx}>
                            <Box sx={getRatingDotSx(rating)} />
                            <Typography sx={ratingTextSx}>{rating}</Typography>
                          </Box>
                          <Typography sx={selectionTextSx}>{selection}</Typography>
                        </Box>

                        {/* Rationale text */}
                        <Typography variant="body2" sx={rationaleTextSx}>
                          {rationale}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Screen reader text */}
              <Typography sx={visuallyHiddenSx}>
                {item.name}. Score: {item.score} out of {selections.length * 3}.
                {selections
                  .map((s) => `${s} relevance: ${item.ratings[s] || 'Unknown'}.`)
                  .join(' ')}
              </Typography>
            </Box>
          </BackrsCard>
        );
      })}
    </Box>
  );
};

// Styles

const containerSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(400px, 100%), 1fr))',
  gap: 2,
  '@container (min-width: 600px)': {
    gap: 3,
  },
};

const emptyContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: 8,
};

/**
 * Card styling - using plain object because BackrsCard doesn't support theme function syntax
 * Token mappings: Gray/900 = #181D27, Colors/Base/White = #FFFFFF, radius-4xl = 1.5rem
 */
const cardSx: SxProps<Theme> = {
  border: '1px solid #181D27', // Gray/900
  borderRadius: '1.5rem', // radius-4xl
  backgroundColor: '#FFFFFF', // Colors/Base/White
  p: 0,
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '5px 5px 0px 0px #181D27', // Gray/900
  },
};

/**
 * Card styling with muting support - plain object for BackrsCard compatibility
 * Token mappings: Gray/900 = #181D27, Colors/Base/White = #FFFFFF, radius-4xl = 1.5rem
 */
const getCardSx = (isMuted: boolean): SxProps<Theme> => ({
  border: '1px solid #181D27', // Gray/900
  borderRadius: '1.5rem', // radius-4xl
  backgroundColor: '#FFFFFF', // Colors/Base/White
  p: 0,
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  opacity: isMuted ? 0.6 : 1,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '5px 5px 0px 0px #181D27', // Gray/900
  },
});

/** Wrapper for card content - provides position: relative for absolute children */
const cardContentWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  py: backrs.spacing['spacing-3xl'],
  px: backrs.spacing['spacing-xl'],
});

/** External link icon - aligned right in header row */
const externalLinkSx: SxProps<Theme> = {
  padding: 0,
  alignSelf: 'flex-start',
  marginLeft: 'auto',
  '&:hover': {
    backgroundColor: 'transparent',
  },
};

/** Header row containing icon and text */
const headerRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 2,
};

/** Featured icon container with mode-dependent background - 56x56px per Figma */
const getIconContainerSx =
  (mode: RelevanceMode): SxProps<Theme> =>
  ({ backrs }) => ({
    width: 56, // Hardcoded: specific Figma design requirement
    height: 56, // Hardcoded: specific Figma design requirement
    borderRadius: backrs.spacing['spacing-lg'],
    border: `2px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    backgroundColor: mode === 'majors' ? backrs.colors['Error/400'] : backrs.colors['Brand/500'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  });

/** Header text column containing type label and title */
const headerTextColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  flex: 1,
  minWidth: 0,
};

/** Type label (e.g., "Major") - shown only for major cards */
const typeLabelSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});

/** Title styling - 18px per Figma */
const titleSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-lg-semibold'],
  color: backrs.colors['Colors/Foreground/fg-primary'],
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

/** Container for rationale items */
const rationaleContainerSx: SxProps<Theme> = {
  mt: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

/** Single rationale item container */
const rationaleItemSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

/** Badge group container - holds rating badge and selection name */
const badgeGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-lg'],
  py: 0.5,
  pl: 0.5,
  pr: 1.25,
  width: 'fit-content',
});

/** Rating badge with colored dot */
const ratingBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-sm'],
  px: 1,
  py: 0.25,
});

/** Colored dot indicator for rating - uses shared RELEVANCE_COLORS */
const getRatingDotSx = (rating: RelevanceRating): SxProps<Theme> => ({
  width: 6, // Hardcoded: small indicator dot size
  height: 6, // Hardcoded: small indicator dot size
  borderRadius: '50%',
  backgroundColor: RELEVANCE_COLORS[rating],
  flexShrink: 0,
});

/** Rating text (High/Medium/Low) */
const ratingTextSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-medium'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

/** Selection name text */
const selectionTextSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-medium'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

/** Rationale text styling */
const rationaleTextSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
});
